import { PURCHASED_EVENT_ADDON_FEATURES } from '../../featurePermissions/helpers';
import { EVENT_ADDON_ITEM_TYPE } from '../../../../../constants/event-items';
import { isAlbum } from '../../../../../util/album';

/**
 * Given the add-ons and a feature, retrieve the respective add-on for the given feature
 *
 * @param {Array} eventAddonItems the event add-on items
 * @param {String} feature the feature to check for, one of PURCHASED_EVENT_ADDON_FEATURES
 * @returns {Object}
 */
export const getAddonForFeature = (eventAddonItems, feature) => {
  if (!eventAddonItems || !feature) {
    return undefined;
  }

  if (feature === PURCHASED_EVENT_ADDON_FEATURES.video_album_enabled) {
    return (eventAddonItems ?? []).find(
      ({ types }) => types[0] === EVENT_ADDON_ITEM_TYPE.GROUP_VIDEO,
    );
  }

  if (feature === PURCHASED_EVENT_ADDON_FEATURES.memory_book_enabled) {
    return (eventAddonItems ?? []).find(
      ({ types }) => types[0] === EVENT_ADDON_ITEM_TYPE.MEMORY_BOOK,
    );
  }

  return (eventAddonItems ?? []).find(
    ({ types }) => types[0] === EVENT_ADDON_ITEM_TYPE.WALDOFIED,
  );
};

/**
 * Given a hierarchy item and a feature, determine if the feature is available for the item
 *
 * @param {Object} hierarchyItem either an album or an albumFolder
 * @param {Boolean} hasActiveSubscription whether or not the user has an active subscription
 * @param {String} feature the feature to check for, one of PURCHASED_EVENT_ADDON_FEATURES
 * @returns {boolean}
 */
export const isAddonFeatureEnabled = (
  hierarchyItem,
  hasActiveSubscription,
  feature,
) => {
  if (!hierarchyItem || !feature) {
    return false;
  }

  const purchasedEventAddonsTypes =
    hierarchyItem?.root_album_folder?.purchased_event_addon_types ??
    hierarchyItem?.purchased_event_addon_types;

  if (
    [PURCHASED_EVENT_ADDON_FEATURES.marketing_prints_enabled].includes(feature)
  ) {
    return true;
  }

  const waldoficationFeatures = [
    PURCHASED_EVENT_ADDON_FEATURES.qr_code_enabled,
    PURCHASED_EVENT_ADDON_FEATURES.free_joins_enabled,
    PURCHASED_EVENT_ADDON_FEATURES.white_label_marketing_prints_enabled,
    PURCHASED_EVENT_ADDON_FEATURES.photo_watermark_enabled,
    PURCHASED_EVENT_ADDON_FEATURES.slideshow_enabled,
    PURCHASED_EVENT_ADDON_FEATURES.members_view_enabled,
    PURCHASED_EVENT_ADDON_FEATURES.custom_tags_enabled,
    PURCHASED_EVENT_ADDON_FEATURES.face_highlights_enabled,
    PURCHASED_EVENT_ADDON_FEATURES.owned_content_free_joins_enabled,
    PURCHASED_EVENT_ADDON_FEATURES.custom_hashtag_enabled,
    PURCHASED_EVENT_ADDON_FEATURES.join_code_enabled,
    PURCHASED_EVENT_ADDON_FEATURES.require_review,
  ];

  if (
    waldoficationFeatures.includes(feature) &&
    purchasedEventAddonsTypes.includes(EVENT_ADDON_ITEM_TYPE.WALDOFIED)
  ) {
    return true;
  }

  const waldoficationProPlusFeatures = [
    PURCHASED_EVENT_ADDON_FEATURES.multi_date_enabled,
  ];

  if (
    waldoficationProPlusFeatures.includes(feature) &&
    purchasedEventAddonsTypes.some(type =>
      [
        EVENT_ADDON_ITEM_TYPE.WALDOFIED_PRO_PLUS,
        EVENT_ADDON_ITEM_TYPE.WALDOFIED_PHOTO_FINDER,
        EVENT_ADDON_ITEM_TYPE.WALDOFIED_PHOTO_FINDER_PLUS,
        EVENT_ADDON_ITEM_TYPE.WALDOFIED_PHOTO_FINDER_PLUS_10K,
        EVENT_ADDON_ITEM_TYPE.WALDOFIED_PHOTO_FINDER_PLUS_25K,
        EVENT_ADDON_ITEM_TYPE.WALDOFIED_PHOTO_FINDER_PLUS_50K,
      ].includes(type),
    )
  ) {
    return true;
  }

  const subscriptionFeatures = [
    PURCHASED_EVENT_ADDON_FEATURES.owned_content_free_joins_enabled,
  ];

  if (hasActiveSubscription && subscriptionFeatures.includes(feature)) {
    return true;
  }

  if (
    feature === PURCHASED_EVENT_ADDON_FEATURES.video_album_enabled &&
    purchasedEventAddonsTypes.some(type =>
      [EVENT_ADDON_ITEM_TYPE.GROUP_VIDEO].includes(type),
    )
  ) {
    return true;
  }

  const specialtyAlbumOnlyFeatures = [
    PURCHASED_EVENT_ADDON_FEATURES.qr_code_enabled,
    PURCHASED_EVENT_ADDON_FEATURES.white_label_marketing_prints_enabled,
    PURCHASED_EVENT_ADDON_FEATURES.slideshow_enabled,
    PURCHASED_EVENT_ADDON_FEATURES.require_review,
  ];

  // @TODO resolve this in a more graceful manner (most likely GQL)
  // This is intended so that features like QR Code Enabled are upsold
  // from the Gallery while still remaining available from the album level.
  // Overall, the idea is for this to cover standalone specialty albums,
  // which should have these features unlocked, but should still upsell
  // them to the user when they are on the gallery level and have a free
  // photos album.
  if (
    isAlbum(hierarchyItem) &&
    specialtyAlbumOnlyFeatures.includes(feature) &&
    purchasedEventAddonsTypes.some(type =>
      [
        EVENT_ADDON_ITEM_TYPE.MEMORY_BOOK,
        EVENT_ADDON_ITEM_TYPE.GROUP_VIDEO,
      ].includes(type),
    )
  ) {
    return true;
  }

  const memoryBookFeatures = [
    PURCHASED_EVENT_ADDON_FEATURES.memory_book_enabled,
    PURCHASED_EVENT_ADDON_FEATURES.qr_code_enabled,
    PURCHASED_EVENT_ADDON_FEATURES.white_label_marketing_prints_enabled,
    PURCHASED_EVENT_ADDON_FEATURES.require_review,
  ];

  if (
    memoryBookFeatures.includes(feature) &&
    purchasedEventAddonsTypes.some(type =>
      [EVENT_ADDON_ITEM_TYPE.MEMORY_BOOK].includes(type),
    )
  ) {
    return true;
  }

  const groupVideoFeatures = [
    PURCHASED_EVENT_ADDON_FEATURES.video_album_enabled,
    PURCHASED_EVENT_ADDON_FEATURES.qr_code_enabled,
    PURCHASED_EVENT_ADDON_FEATURES.white_label_marketing_prints_enabled,
    PURCHASED_EVENT_ADDON_FEATURES.require_review,
  ];

  if (
    groupVideoFeatures.includes(feature) &&
    purchasedEventAddonsTypes.some(type =>
      [EVENT_ADDON_ITEM_TYPE.GROUP_VIDEO].includes(type),
    )
  ) {
    return true;
  }

  return false;
};

/**
 * Given a set of addOns and a feature, determine if the feature is available for the addons
 *
 * @param {Array} eventAddons the event addons to take into consideration
 * @param {String} feature the feature to check for, one of PURCHASED_EVENT_ADDON_FEATURES
 * @returns {boolean}
 */
export const isAddonFeatureEnabledForAddons = (eventAddons, feature) => {
  if (!eventAddons || !feature) {
    return false;
  }

  if (
    [PURCHASED_EVENT_ADDON_FEATURES.marketing_prints_enabled].includes(feature)
  ) {
    return true;
  }

  const waldoficationFeatures = [
    PURCHASED_EVENT_ADDON_FEATURES.qr_code_enabled,
    PURCHASED_EVENT_ADDON_FEATURES.white_label_marketing_prints_enabled,
    PURCHASED_EVENT_ADDON_FEATURES.free_joins_enabled,
    PURCHASED_EVENT_ADDON_FEATURES.photo_watermark_enabled,
    PURCHASED_EVENT_ADDON_FEATURES.slideshow_enabled,
    PURCHASED_EVENT_ADDON_FEATURES.members_view_enabled,
    PURCHASED_EVENT_ADDON_FEATURES.custom_tags_enabled,
    PURCHASED_EVENT_ADDON_FEATURES.face_highlights_enabled,
    PURCHASED_EVENT_ADDON_FEATURES.owned_content_free_joins_enabled,
    PURCHASED_EVENT_ADDON_FEATURES.custom_hashtag_enabled,
    PURCHASED_EVENT_ADDON_FEATURES.join_code_enabled,
    PURCHASED_EVENT_ADDON_FEATURES.require_review,
  ];

  if (
    waldoficationFeatures.includes(feature) &&
    eventAddons.find(({ types }) =>
      [
        EVENT_ADDON_ITEM_TYPE.WALDOFIED,
        EVENT_ADDON_ITEM_TYPE.WALDOFIED_PRO,
        EVENT_ADDON_ITEM_TYPE.WALDOFIED_PRO_PLUS,
        EVENT_ADDON_ITEM_TYPE.WALDOFIED_PHOTO_FINDER,
        EVENT_ADDON_ITEM_TYPE.WALDOFIED_PHOTO_FINDER_PLUS,
        EVENT_ADDON_ITEM_TYPE.WALDOFIED_PHOTO_FINDER_PLUS_10K,
        EVENT_ADDON_ITEM_TYPE.WALDOFIED_PHOTO_FINDER_PLUS_25K,
        EVENT_ADDON_ITEM_TYPE.WALDOFIED_PHOTO_FINDER_PLUS_50K,
      ].includes(types[0]),
    )
  ) {
    return true;
  }

  const waldoficationProPlusFeatures = [
    PURCHASED_EVENT_ADDON_FEATURES.multi_date_enabled,
  ];

  if (
    waldoficationProPlusFeatures.includes(feature) &&
    eventAddons.find(({ types }) =>
      [
        EVENT_ADDON_ITEM_TYPE.WALDOFIED_PRO_PLUS,
        EVENT_ADDON_ITEM_TYPE.WALDOFIED_PHOTO_FINDER,
        EVENT_ADDON_ITEM_TYPE.WALDOFIED_PHOTO_FINDER_PLUS,
        EVENT_ADDON_ITEM_TYPE.WALDOFIED_PHOTO_FINDER_PLUS_10K,
        EVENT_ADDON_ITEM_TYPE.WALDOFIED_PHOTO_FINDER_PLUS_25K,
        EVENT_ADDON_ITEM_TYPE.WALDOFIED_PHOTO_FINDER_PLUS_50K,
      ].includes(types[0]),
    )
  ) {
    return true;
  }

  const groupVideoFeatures = [
    PURCHASED_EVENT_ADDON_FEATURES.video_album_enabled,
    PURCHASED_EVENT_ADDON_FEATURES.qr_code_enabled,
    PURCHASED_EVENT_ADDON_FEATURES.white_label_marketing_prints_enabled,
    PURCHASED_EVENT_ADDON_FEATURES.require_review,
  ];

  if (
    groupVideoFeatures.includes(feature) &&
    eventAddons.find(
      ({ types }) => types[0] === EVENT_ADDON_ITEM_TYPE.GROUP_VIDEO,
    )
  ) {
    return true;
  }

  const memoryBookFeatures = [
    PURCHASED_EVENT_ADDON_FEATURES.memory_book_enabled,
    PURCHASED_EVENT_ADDON_FEATURES.qr_code_enabled,
    PURCHASED_EVENT_ADDON_FEATURES.white_label_marketing_prints_enabled,
    PURCHASED_EVENT_ADDON_FEATURES.require_review,
  ];

  if (
    memoryBookFeatures.includes(feature) &&
    eventAddons.find(
      ({ types }) => types[0] === EVENT_ADDON_ITEM_TYPE.MEMORY_BOOK,
    )
  ) {
    return true;
  }

  return false;
};
